import { useMemo } from 'react';
import { availabilityRelationsSelector } from 'slices/availabilityRelation.slice';
import { membershipsSelector } from 'slices/memberships.slice';
import {
  selectObjectTypesMap,
  selectObjectTypesTree,
} from 'slices/objectTypes.slice';
import { selectedReservationTemplateMap } from 'slices/reservationTemplate.slice';
import { useAppSelector } from 'slices/store';
import { getMemberTypes, getOptionalRelatedTypes } from 'utils/associations';
import { getSubTypes } from 'utils/objectTypes';
import { TType } from '@timeedit/types';

const useShowAssociations = (objectType: TType | null) => {
  const memberships = useAppSelector(membershipsSelector);
  const objectTypesMap = useAppSelector(selectObjectTypesMap);
  const availabilityRelations = useAppSelector(availabilityRelationsSelector);
  const typeTree = useAppSelector(selectObjectTypesTree);
  const reservationTemplatesMap = useAppSelector(
    selectedReservationTemplateMap
  );

  const showMembers = useMemo(
    () =>
      getMemberTypes([objectType?.id], memberships, objectTypesMap).length > 0,
    [objectType?.id, memberships, objectTypesMap]
  );
  const showAvailabilityRelated = useMemo(
    () =>
      availabilityRelations.some(({ availabilityTypes = [] }) =>
        availabilityTypes
          .flatMap((t) => getSubTypes(Number(t.type), typeTree))
          .some((t) => t === objectType?.id)
      ),
    [objectType?.id, availabilityRelations, typeTree]
  );
  const showOptionalRelated = useMemo(() => {
    return (
      getOptionalRelatedTypes(
        [objectType?.id],
        objectTypesMap,
        typeTree,
        reservationTemplatesMap
      ).length > 0
    );
  }, [typeTree, objectTypesMap, reservationTemplatesMap, objectType?.id]);

  return {
    showMembers,
    showAvailabilityRelated,
    showOptionalRelated,
    showRelations: true,
  };
};

export default useShowAssociations;
